// ============================================================
// reporter.js — tenant-level "Report a problem" data layer.
//
// U1 (2026-04-26) — DESIGN_LOG U1 sub-decisions 1+4.
//
// Lives in src/platform/ because reporting is CROSS-CUTTING — every
// page on every tenant uses it regardless of which modules are enabled.
// Not a module (would force every tenant to enable a "reports" module
// for the topbar button to appear, which defeats "report from anywhere").
//
// Collection: tenants/{tid}/reports/{id}
// Storage:    tenants/{tid}/reports/{id}/photo.{ext}
//
// Doc shape (data-architect approved — DESIGN_LOG U1 sub-decision 4):
//   text:        string (≤2000 chars)
//   pageUrl:     string (window.location.hash at submit)
//   pageLabel:   string (route label, e.g. "Inventory")
//   userEmail:   string
//   userName:    string
//   userRole:    string
//   userAgent:   string (sliced 200 chars)
//   photoUrl?:   string
//   photoPath?:  string
//   status:      'new' | 'in-review' | 'resolved' | 'archived'
//   createdAt:   serverTimestamp
//   resolvedAt?: serverTimestamp
//   resolvedBy?: string
//   resolutionNote?: string
//
// Doc id: RPT-YYYYMMDD-{6char-random} (sortable + collision-resistant).
// ============================================================

import {
  setDoc, updateDoc,
  serverTimestamp, query, where, orderBy, onSnapshot
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage, auth } from '../firebase.js';
import { tenantCol, tenantDoc, tenantStoragePath } from './tenant.js';

const REPORT_TEXT_MAX = 2000;

function _newReportId() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  // 6 random alphanumerics — collision per day is ~1 in 2^31 even at hundreds/day.
  let rand = '';
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';   // omit 0/O/1/I/L for legibility
  for (let i = 0; i < 6; i++) rand += chars[Math.floor(Math.random() * chars.length)];
  return `RPT-${y}${m}${day}-${rand}`;
}

/**
 * Submit a new report.
 *   payload: { text, pageUrl, pageLabel, userName, userRole, photoFile? }
 * Returns the created doc id.
 *
 * The photoFile is uploaded FIRST (so the doc carries a stable URL on
 * first read). If the photo upload fails, the whole submit throws —
 * caller decides whether to retry without the photo.
 */
export async function submitReport(payload) {
  const u = auth.currentUser;
  if (!u) throw new Error('Not signed in');

  const text = String(payload.text || '').slice(0, REPORT_TEXT_MAX).trim();
  if (!text) throw new Error('Please write what happened before submitting.');

  const id = _newReportId();
  let photoUrl = null;
  let photoPath = null;

  if (payload.photoFile) {
    if (payload.photoFile.size > 5 * 1024 * 1024) {
      throw new Error('Photo must be under 5 MB.');
    }
    const ext = (payload.photoFile.name && payload.photoFile.name.match(/\.([a-z0-9]{2,5})$/i)) ? payload.photoFile.name.split('.').pop().toLowerCase() : 'jpg';
    photoPath = tenantStoragePath('reports') + '/' + id + '/photo.' + ext;
    const r = storageRef(storage, photoPath);
    await uploadBytes(r, payload.photoFile, { contentType: payload.photoFile.type || 'image/jpeg' });
    photoUrl = await getDownloadURL(r);
  }

  await setDoc(tenantDoc('reports', id), {
    text,
    pageUrl:   String(payload.pageUrl   || window.location.hash || ''),
    pageLabel: String(payload.pageLabel || ''),
    userEmail: u.email || '',
    userName:  String(payload.userName  || u.displayName || u.email || ''),
    userRole:  String(payload.userRole  || ''),
    userAgent: String(navigator.userAgent || '').slice(0, 200),
    photoUrl:  photoUrl,
    photoPath: photoPath,
    status:    'new',
    createdAt: serverTimestamp()
  });
  return id;
}

/**
 * Subscribe to all reports for the current tenant. Director/admin only
 * (Rules enforce; this helper does NOT role-check — caller does).
 *   onChange(reports[])
 *   opts: { statusFilter?: string, includeArchived?: boolean }
 * Returns unsubscribe fn.
 */
export function subscribeReports(onChange, opts = {}) {
  const constraints = [orderBy('createdAt', 'desc')];
  if (opts.statusFilter) {
    constraints.unshift(where('status', '==', opts.statusFilter));
  } else if (!opts.includeArchived) {
    // Default: hide archived — director sees new + in-review + resolved.
    constraints.unshift(where('status', 'in', ['new', 'in-review', 'resolved']));
  }
  const q = query(tenantCol('reports'), ...constraints);
  return onSnapshot(q, (snap) => {
    const rows = [];
    snap.forEach(d => rows.push({ id: d.id, ...d.data() }));
    onChange(rows);
  }, (err) => {
    console.warn('[reporter.subscribeReports]', err);
    onChange([]);
  });
}

/** Subscribe to count of NEW reports (for topbar badge). */
export function subscribeNewReportCount(onCount) {
  const q = query(tenantCol('reports'), where('status', '==', 'new'));
  return onSnapshot(q, (snap) => onCount(snap.size), () => onCount(0));
}

export async function setReportStatus(id, status, opts = {}) {
  const u = auth.currentUser;
  if (!u) throw new Error('Not signed in');
  if (!['new', 'in-review', 'resolved', 'archived'].includes(status)) {
    throw new Error('Bad status: ' + status);
  }
  const update = { status };
  if (status === 'resolved' || status === 'archived') {
    update.resolvedAt = serverTimestamp();
    update.resolvedBy = u.email || '';
    if (opts.note) update.resolutionNote = String(opts.note).slice(0, 500);
  }
  await updateDoc(tenantDoc('reports', id), update);
}

/** Delete the photo blob in Storage (called after archive if cleanup desired). Best-effort. */
export async function deleteReportPhoto(photoPath) {
  if (!photoPath) return;
  try { await deleteObject(storageRef(storage, photoPath)); }
  catch (e) { console.warn('[reporter.deletePhoto]', e); }
}
